/**
 * Transactions, Acquisitions & Brand Sales — 10 documents.
 *
 * None of these entries carry a template and none ever will. These are attorney-led transaction
 * documents drafted by the buyer's counsel and negotiated by yours. The entries exist so a founder
 * can follow the process, understand what each document decides, and prepare for the part they
 * actually control: what diligence will ask for, and what a buyer discounts for when it is missing.
 * Nothing here is a substitute for counsel and an accountant engaged before the first letter is
 * signed, not after.
 */
export const MA_DOCS = [
  {
    id: "letter-of-intent",
    name: "Letter of Intent",
    category: "ma",
    subcategory: "Deal process",
    aliases: ["LOI", "Heads of Terms", "Indication of Interest", "Term Sheet (M&A)"],
    stages: ["growth", "exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Seller (Company and/or Founders) + Buyer",
    whenYouNeedIt:
      "When a buyer's interest becomes concrete enough to put terms on paper — usually the first document in a sale process, and the one that quietly sets everything that follows.",
    whatItDoes:
      "Describes the proposed deal: price, structure, what is included, the diligence scope and timetable, and which handful of provisions bind immediately — typically confidentiality, exclusivity and costs.",
    whyItMatters:
      "The LOI is where the price is set and where leverage changes hands. Once you sign exclusivity you are off the market, and the buyer then has weeks or months to find reasons to move the number — and in a CPG diligence they will find some, because almost every young brand has paperwork gaps. Everything left vague in the LOI gets resolved in the buyer's favour in the definitive documents, so the things you actually care about — what happens to your team, whether you stay, how any earn-out is measured — belong on paper while you still have alternatives.",
    majorTerms: [
      "Purchase price and how it is calculated",
      "Deal structure: assets, stock or membership interests",
      "What is included and what is excluded",
      "Cash at closing, holdback or escrow, and any earn-out",
      "Working capital and inventory treatment",
      "Treatment of company debt and founder guarantees",
      "Employment or consulting arrangements for founders",
      "Exclusivity period",
      "Diligence scope and timetable",
      "Which provisions are binding",
      "Conditions to closing and expenses",
    ],
    clausesToWatch: [
      {
        clause: "Exclusivity period and any automatic extension",
        why: "It is binding when most of the letter is not, and every week of it is a week you have no alternative buyer to point at.",
      },
      {
        clause: "Whether price is stated as enterprise value or as cash to the seller",
        why: "The difference is debt, working capital and transaction costs, and founders routinely celebrate a number that is not the number they receive.",
      },
      {
        clause: "Working capital and inventory adjustment mechanics",
        why: "For a brand, inventory is a large part of the balance sheet, and an adjustment formula written loosely moves real money at closing.",
      },
      {
        clause: "The earn-out described in a single line",
        why: "One sentence in the LOI becomes twenty pages later, drafted by the buyer's counsel against a metric you never defined.",
      },
      {
        clause: "Conditions to closing",
        why: "Conditions written broadly are effectively an option for the buyer while you remain locked up and unable to talk to anyone else.",
      },
      {
        clause: "Expense reimbursement",
        why: "Agreeing to cover the buyer's costs whether or not the deal closes turns a failed process into a bill.",
      },
      {
        clause: "Treatment of founder guarantees",
        why: "If their release is not raised here, you can sell the business and still be standing behind its obligations afterwards.",
      },
    ],
    cpgIssues: [
      "Inventory is a live negotiation, not a line item: how it is valued, whether short-dated or slow-moving stock counts at all, and who bears obsolete packaging are all decided in the price mechanics.",
      "Diligence will ask for the paperwork most young brands do not have — signed co-packer agreements, formula ownership, contractor IP assignments, trademark registrations in the company's name, COAs and quality records, executed retailer vendor agreements — and every gap becomes a price conversation.",
      "Distributor agreements that cannot be terminated, or that carry change-of-control and anti-assignment provisions, hand your distributor leverage at the exact moment you have least; identify them before you sign, not when the buyer's counsel finds them.",
      "Trade spend, deductions, spoils and free fill mean the revenue number in the LOI needs a definition — a multiple quoted against gross revenue and then applied to net is a very different price.",
      "Multiples and structures vary widely by category, size, growth rate and buyer type: a strategic acquirer, a platform and a financial buyer will not value the same brand the same way, so treat any number quoted to you as 'the market' as one buyer's view rather than a benchmark.",
    ],
    founderMistakes: [
      "Signing a long exclusivity with automatic extensions and losing every alternative before diligence had even started.",
      "Agreeing a headline enterprise value and never modelling what actually reached the shareholders after debt, escrow and costs.",
      "Leaving the earn-out described in one sentence and negotiating the real terms with no leverage left.",
      "Not raising an un-terminable distributor agreement until the buyer's counsel found it in week three.",
      "Going into diligence with unsigned contractor agreements and watching the price move against them.",
    ],
    negotiationPoints: [
      "Ask for the shortest exclusivity that allows genuine diligence, with a hard expiry and no automatic extension.",
      "Ask for the price expressed as cash to the seller at closing, with every adjustment spelled out.",
      "Ask that the earn-out metric, measurement period and decision rights are defined in the LOI rather than later.",
      "Ask for the buyer's full diligence request list up front so you can start closing gaps immediately.",
      "Ask that founder guarantees are released at closing, and say so in the letter.",
    ],
    redFlags: [
      "Exclusivity with automatic extensions at the buyer's option.",
      "A price that begins moving as soon as diligence starts, with no defined mechanism.",
      "An earn-out with no metric named.",
      "A buyer who will not provide a diligence list.",
      "Expense reimbursement payable whether or not the deal closes.",
      "Pressure to sign before you have counsel engaged.",
    ],
    beforeYouSign: [
      "A signed diligence NDA covering everything you are about to disclose.",
      "Your own diligence readiness review, with every paperwork gap listed honestly.",
      "A model of what the price actually delivers to each shareholder after debt, escrow and costs.",
      "Counsel and an accountant engaged before the letter, not after it.",
    ],
    related: [
      "no-shop-agreement",
      "due-diligence-nda",
      "asset-purchase-agreement",
      "ma-stock-purchase-agreement",
      "earn-out-agreement",
      "investor-letter-of-intent",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "The LOI binds you to exclusivity and sets the price and structure that everything else is drafted from, so counsel belongs in the first draft rather than the last.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "asset-purchase-agreement",
    name: "Asset Purchase Agreement",
    category: "ma",
    subcategory: "Purchase agreements",
    aliases: ["APA", "Asset Sale Agreement", "Purchase and Sale of Assets"],
    stages: ["exit"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Selling Company + Buyer (with founders signing certain provisions personally)",
    whenYouNeedIt:
      "When the buyer wants the business but not the entity — the most common structure for a first CPG brand sale.",
    whatItDoes:
      "Sells named assets and assigns named contracts, with the buyer taking on only the liabilities it agrees to take. Your company itself stays with you, along with everything that was not on the list.",
    whyItMatters:
      "In an asset deal the buyer picks what it wants: the brand, the formula, the inventory, the co-packer relationship, some contracts and not others. That makes the schedules the real document — an asset left off a list does not transfer, and a contract that needs the counterparty's consent to move does not move just because you signed. This is where CPG relationships get tested, because distributor and co-packer agreements frequently require consent to assign or contain change-of-control provisions, and the counterparty knows exactly how badly you need their signature. Buyers and sellers often prefer different structures for reasons that include tax; that comparison belongs with your accountant and counsel before the LOI.",
    majorTerms: [
      "Assets included and excluded, by schedule",
      "Contracts assigned and the consents required to assign them",
      "Liabilities assumed and liabilities expressly excluded",
      "Purchase price and its allocation",
      "Inventory valuation and the closing cut-off",
      "Working capital adjustment",
      "Escrow or holdback and its release",
      "Representations and warranties",
      "Indemnification, caps, baskets and survival periods",
      "Non-compete and non-solicit from the seller",
      "Employee transfer arrangements",
      "Transition services",
    ],
    clausesToWatch: [
      {
        clause: "The asset schedules",
        why: "Anything not listed stays with you, including things founders assume are obvious — the social handles, the customer list, the artwork files, the domain and the seller accounts.",
      },
      {
        clause: "Required third-party consents",
        why: "A distributor or co-packer whose consent is needed can extract terms in the middle of your closing, and there is no version of that conversation where you have the stronger position.",
      },
      {
        clause: "Assumed versus excluded liabilities",
        why: "Everything not assumed stays with your company, which still has to deal with it after the money has been distributed.",
      },
      {
        clause: "Inventory valuation and date-code treatment",
        why: "Short-dated and slow-moving stock is where a clean price becomes a negotiation on the day of closing.",
      },
      {
        clause: "Representations, warranties and their survival",
        why: "You are standing behind statements about IP, contracts and compliance for a period after closing, and unassigned artwork or an unsigned co-packer agreement becomes your personal exposure.",
      },
      {
        clause: "Indemnification cap and escrow",
        why: "Without a cap and a defined release date, part of your purchase price is permanently at risk with no end point.",
      },
      {
        clause: "Seller non-compete scope",
        why: "A non-compete written across all food and beverage rather than your actual category can stop you working again in the only industry you know.",
      },
    ],
    cpgIssues: [
      "Every asset the business actually runs on has to be listed: the formula and specifications, artwork and dielines, trademark registrations, the GS1 company prefix and UPCs, retailer vendor records, marketplace and Shopify accounts, COAs and quality records, and the co-packer's specification file.",
      "Distributor and co-packer agreements commonly require consent to assign or contain change-of-control provisions, so identify them early and plan the conversation rather than discovering it at signing.",
      "Inventory sitting at a 3PL, at a co-packer and in transit all has to be counted, dated and valued at closing, and any lender's arrangements over that inventory have to be cleared first.",
      "Open trade spend, unbilled deductions and pending chargebacks from retailers land after closing, and who bears them is decided in this document or not at all.",
      "Responsibility for product made before closing does not disappear because the assets moved; how it is allocated and insured is one of the harder negotiations in a food or beverage deal and is firmly counsel's territory.",
    ],
    founderMistakes: [
      "Leaving the Instagram handle, the customer email list and the product photography off the asset schedule.",
      "Assuming a distributor agreement would transfer automatically and discovering it needed consent two days before closing.",
      "Not clearing a lender's arrangements over inventory until the buyer's counsel raised it.",
      "Agreeing an inventory valuation with no allowance for date codes, then arguing about it at the closing count.",
      "Giving broad warranties with no cap and no survival limit because the deal felt friendly.",
    ],
    negotiationPoints: [
      "Ask to build the asset schedule yourself, early, from a real inventory of everything the business runs on.",
      "Ask for a defined process and timetable for third-party consents, with an agreed outcome if one is refused.",
      "Ask for caps on indemnification and a defined survival period for warranties.",
      "Ask that the inventory valuation method, including date-code treatment, is agreed before closing rather than at it.",
      "Ask for a non-compete scoped to your actual category and to a duration you can live with.",
    ],
    redFlags: [
      "Open-ended indemnification with no cap.",
      "A non-compete broad enough to stop you working in food or beverage at all.",
      "Asset schedules the buyer prepares and you have not read line by line.",
      "Assumed liabilities described by exception, with an incomplete list of exceptions.",
      "No plan at all for consents you already know you need.",
    ],
    beforeYouSign: [
      "A complete written inventory of every asset the business uses and where each one sits.",
      "The list of contracts requiring consent, with those conversations already started.",
      "A dated, counted inventory position across every location.",
      "Counsel and an accountant on the structure before the LOI was signed, not after.",
    ],
    related: [
      "letter-of-intent",
      "ma-stock-purchase-agreement",
      "brand-acquisition-agreement",
      "ip-purchase-agreement",
      "transition-services-agreement",
      "earn-out-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "The schedules, consents and indemnities decide what actually transfers and what you keep standing behind afterwards, and none of it should be drafted without counsel.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "ma-stock-purchase-agreement",
    name: "Stock Purchase Agreement (M&A)",
    category: "ma",
    subcategory: "Purchase agreements",
    aliases: ["SPA", "Share Purchase Agreement", "Equity Purchase Agreement"],
    stages: ["exit"],
    importance: "advanced",
    entityTypes: ["corporation"],
    parties: "Selling Shareholders + Buyer (with the Company as a party)",
    whenYouNeedIt:
      "When the buyer is buying the company itself rather than a list of its assets — usually because continuity matters, or because the shareholders prefer that structure.",
    whatItDoes:
      "Sells the shares of the company. The buyer takes the entity as it stands, with its contracts, its history and its liabilities, and the shareholders rather than the company receive the money.",
    whyItMatters:
      "Because the company continues unchanged, contracts generally stay where they are instead of being assigned one by one — which is why sellers often prefer this structure and why buyers price for the risk of inheriting everything, including problems nobody has found. That risk is managed through the representations and warranties you give and the indemnities standing behind them, so the negotiation moves from schedules to promises you make personally. Many CPG contracts also carry change-of-control provisions, so a distributor or co-packer may still have a say even though nothing is being assigned. What each structure means legally and for tax is a conversation for counsel and your accountant before the LOI.",
    majorTerms: [
      "Price per share and total consideration",
      "Which holders sell and whether minority holders can be dragged along",
      "Escrow or holdback and its release schedule",
      "Representations and warranties from the company and from individual sellers",
      "Indemnification: caps, baskets, survival and whether liability is joint or several",
      "Working capital and debt adjustments",
      "Treatment of options and outstanding convertible instruments",
      "Change-of-control consents required under material contracts",
      "Non-compete and non-solicit from each founder",
      "Founder employment or consulting terms",
      "Closing conditions",
    ],
    clausesToWatch: [
      {
        clause: "Which holders must sign and whether a minority can block",
        why: "A single small holder you cannot reach can hold up a closing entirely if the documents do not provide for it.",
      },
      {
        clause: "Joint versus several liability among sellers",
        why: "Joint liability means one seller's problem can become every seller's problem, including founders who held small stakes.",
      },
      {
        clause: "Escrow size and release timing",
        why: "It is your money held back against risks the buyer has identified, and without a defined release date it stays there indefinitely.",
      },
      {
        clause: "Survival periods and indemnity caps",
        why: "They decide how long and how far your personal exposure runs after the money has been distributed and spent.",
      },
      {
        clause: "Treatment of outstanding SAFEs, notes and options",
        why: "Everything unconverted has to be dealt with before closing, and each one is a signature and a calculation nobody wants to be doing in the final week.",
      },
      {
        clause: "Change-of-control consents",
        why: "Even though contracts are not being assigned, a distributor or co-packer with a change-of-control provision can still require consent, on their timetable rather than yours.",
      },
    ],
    cpgIssues: [
      "A cluttered cap table is a real obstacle here: every small angel, un-exercised option and unconverted instrument has to be dealt with, and one unreachable holder can stall a closing.",
      "Change-of-control provisions in distributor, co-packer and retailer agreements can require notice or consent even when the entity is unchanged, and the counterparty controls the timing.",
      "The company keeps its history, so responsibility for product made before closing, quality records, past recalls and open retailer deductions all come with it — which is why buyers ask for COAs, batch records and complaint logs.",
      "Undocumented equity — a promise made to a broker, a distributor's principal or an early advisor — surfaces as a claim against the very shares being sold.",
      "Facility registrations, certifications, licences and the GS1 prefix are issued to the entity, so keeping the entity keeps them, and that continuity is often the reason this structure is chosen at all.",
    ],
    founderMistakes: [
      "Discovering in diligence that founder shares had never been properly issued and having to fix it under deal pressure.",
      "Being unable to locate a small early investor whose signature was required to close.",
      "Agreeing joint liability with co-founders without understanding what it meant for the one with the smallest stake.",
      "Not disclosing a verbal equity promise, which became an indemnity claim after closing.",
      "Leaving option holders unaddressed until the final week of the process.",
    ],
    negotiationPoints: [
      "Ask for several rather than joint liability among sellers, with individual caps proportionate to proceeds.",
      "Ask for an escrow sized and timed against real, identified risk, with a defined release date.",
      "Ask for defined survival periods on every warranty rather than open-ended exposure.",
      "Ask that cap table clean-up begins before diligence rather than during it.",
      "Ask for a defined process and timetable for the change-of-control consents you know you need.",
    ],
    redFlags: [
      "Uncapped indemnification.",
      "Joint and several liability across all sellers with no individual cap.",
      "An escrow with no defined release date.",
      "Warranties surviving indefinitely.",
      "A cap table with holders you cannot reach or instruments nobody has modelled.",
    ],
    beforeYouSign: [
      "A fully reconciled cap table with every instrument converted, exercised or documented.",
      "Signed IP assignments from every founder, employee and contractor.",
      "A change-of-control review of every material contract.",
      "Counsel and an accountant engaged on structure before the LOI.",
    ],
    related: [
      "letter-of-intent",
      "asset-purchase-agreement",
      "membership-interest-purchase-agreement",
      "cap-table-documentation",
      "earn-out-agreement",
      "transition-services-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "You are selling the company with its entire history, and the warranties and indemnities you give are personal promises that outlive the closing.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "membership-interest-purchase-agreement",
    name: "Membership Interest Purchase Agreement",
    category: "ma",
    subcategory: "Purchase agreements",
    aliases: ["MIPA", "LLC Interest Purchase Agreement", "Unit Purchase Agreement"],
    stages: ["exit"],
    importance: "advanced",
    entityTypes: ["llc"],
    parties: "Selling Members + Buyer (with the LLC as a party)",
    whenYouNeedIt:
      "When the company being sold is an LLC and the buyer is acquiring the membership interests rather than the assets — the most common exit structure for a first-time CPG brand that never converted.",
    whatItDoes:
      "Sells the membership interests in the LLC: the equivalent of a stock sale for a company that has members and units rather than shareholders and shares.",
    whyItMatters:
      "Most first-time CPG brands are LLCs, and this is the document that sells one. The mechanics resemble a stock deal, but your operating agreement governs what is actually possible: who must consent, what transfer restrictions apply, how capital accounts and distributions are treated at closing, and whether a reluctant member can be brought along. Founders who never completed their operating agreement, or who admitted members by email and nothing else, find that out here — when the buyer's counsel asks for the ownership record and there is not one. Tax outcomes differ meaningfully between structures for an LLC, and that conversation belongs with your accountant and counsel before the LOI.",
    majorTerms: [
      "Interests sold and by which members",
      "Purchase price and its allocation among members",
      "Consents required under the operating agreement",
      "Drag-along and transfer restrictions",
      "Treatment of capital accounts and pre-closing distributions",
      "Working capital and debt adjustments",
      "Representations and warranties",
      "Indemnification, escrow and survival",
      "Non-compete and non-solicit from each member",
      "Manager and officer resignations",
      "Closing conditions",
    ],
    clausesToWatch: [
      {
        clause: "Consents required by the operating agreement",
        why: "Your own governing document decides who has to agree, and if it was never completed nobody can say with confidence who that is.",
      },
      {
        clause: "Drag-along provisions",
        why: "Without one, a small member who does not want to sell can stall the entire process, and adding one mid-deal requires their agreement anyway.",
      },
      {
        clause: "Capital accounts and pre-closing distributions",
        why: "Distributions taken through the year affect the closing adjustment, which surprises founders who treated the company account as an extension of their own.",
      },
      {
        clause: "Allocation of price among members",
        why: "Where percentages and capital accounts differ, the split is not obvious, and it is far easier to agree before signing than in the week of closing.",
      },
      {
        clause: "Indemnity structure among selling members",
        why: "It decides whether a member with a small stake carries the same exposure as the founder who ran the business.",
      },
      {
        clause: "Non-compete on each member",
        why: "Members who were never operationally involved may be asked to sign restrictions that affect their other work.",
      },
    ],
    cpgIssues: [
      "LLC brands frequently admitted early members informally — a friend who funded a can order, a chef who helped with the recipe — and the only record is an email; a buyer will want a clean, evidenced ownership schedule.",
      "Distributions and guaranteed payments made to founder-members through the year feed into the closing adjustment, which is a genuine surprise to founders who never separated the two.",
      "Many operating agreements have no drag-along at all, so one reluctant member can stall a sale for reasons that have nothing to do with price.",
      "Facility registrations, certifications and the GS1 prefix are held by the LLC and continue with it, which is often exactly why the buyer wants the interests rather than the assets.",
      "The same change-of-control questions in distributor and co-packer agreements apply, so review them even though nothing is being assigned.",
    ],
    founderMistakes: [
      "Never completing the ownership schedule in the operating agreement and having to reconstruct it under deal pressure.",
      "Taking distributions through the year with no plan for how they would affect the closing adjustment.",
      "Discovering there was no drag-along only after a small member signalled resistance.",
      "Not disclosing an informal promise of a percentage made to an early helper.",
      "Agreeing an allocation of proceeds verbally and finding the members disagreed at signing.",
    ],
    negotiationPoints: [
      "Ask counsel to reconcile the operating agreement against the actual ownership record before diligence begins.",
      "Ask for a written allocation of proceeds agreed by every member before signing.",
      "Ask for individual caps on each member's indemnity exposure, proportionate to what they receive.",
      "Ask for a clear, agreed treatment of pre-closing distributions.",
      "Ask that every consent required by the operating agreement is obtained on a set timetable.",
    ],
    redFlags: [
      "No completed ownership schedule anywhere in the company's records.",
      "Members admitted with no documentation at all.",
      "No drag-along, with a member already signalling resistance.",
      "Joint and several indemnity across all members regardless of stake.",
      "An allocation of price nobody has agreed in writing.",
    ],
    beforeYouSign: [
      "The operating agreement and every amendment, read against the actual ownership record.",
      "A written allocation of proceeds agreed by all members.",
      "The consent list drawn from both the operating agreement and material contracts.",
      "An accountant on the structure before the LOI.",
    ],
    related: [
      "ma-stock-purchase-agreement",
      "asset-purchase-agreement",
      "llc-operating-agreement",
      "multi-member-llc-operating-agreement",
      "letter-of-intent",
      "earn-out-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "What is possible in an LLC sale is governed by your operating agreement, and the consent, drag-along and capital-account questions need counsel long before a buyer's paperwork arrives.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "brand-acquisition-agreement",
    name: "Brand Acquisition Agreement",
    category: "ma",
    subcategory: "Brand & IP deals",
    aliases: ["Brand Purchase Agreement", "Brand Sale Agreement", "Label Acquisition Agreement"],
    stages: ["exit"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Brand Owner + Acquirer",
    whenYouNeedIt:
      "When a buyer wants the brand itself — the name, the formula, the packaging, the accounts and the customer base — rather than your company; and when you are the buyer picking up a brand from a founder who is winding down.",
    whatItDoes:
      "Transfers a defined brand as a package: trademarks, formulas and specifications, artwork, product listings, customer and subscriber data, supplier and co-packer relationships, and inventory — usually leaving the selling entity behind.",
    whyItMatters:
      "Brand deals are common in CPG because the brand is often the only thing worth buying: a founder who has run out of working capital may still own a name with shelf presence and a formula that works. The risk is that a brand is not one asset but thirty, and the ones founders forget are precisely the ones that stop a buyer trading — the trademark registered in a personal name, the artwork files held by a designer who was never paid to assign them, the co-packer's specification file, the retailer vendor records, the GS1 prefix and the subscriber list. Anything not identified does not move, and the buyer finds out after closing, when it cannot ship.",
    majorTerms: [
      "Trademarks and applications, listed by registration",
      "Formula, specifications and process documentation",
      "Artwork, dielines and photography, with ownership evidence",
      "Domains, social accounts and marketplace seller accounts",
      "Customer, subscriber and wholesale account data",
      "Inventory and packaging components",
      "Co-packer and supplier introductions or assignments",
      "Retailer vendor records and existing listings",
      "GS1 company prefix and UPC treatment",
      "Transition support from the founder",
      "Non-compete",
      "Price, escrow and adjustments",
    ],
    clausesToWatch: [
      {
        clause: "The asset list, item by item",
        why: "A brand is a collection of thirty small things, and whatever is not named simply stays behind with the seller.",
      },
      {
        clause: "Evidence of ownership for each item",
        why: "An invoice is not an assignment, and a designer who was paid but never signed anything is a gap the buyer will price for.",
      },
      {
        clause: "Whether the co-packer and suppliers will work with the buyer",
        why: "A brand nobody can manufacture is a name, and that conversation has to happen before closing rather than after.",
      },
      {
        clause: "Customer and subscriber data transfer",
        why: "A list carries its own consent questions for counsel, and one that cannot properly be transferred is worth far less than the seller assumes.",
      },
      {
        clause: "Founder non-compete scope",
        why: "Written broadly it can prevent you from working in food or beverage at all, rather than just in the category you sold.",
      },
      {
        clause: "Transition support scope and duration",
        why: "Without hours and an end date, 'reasonable assistance' becomes months of unpaid work after the money has already been paid.",
      },
    ],
    cpgIssues: [
      "Trademarks registered in a founder's personal name, in a dissolved entity, or never registered at all are the single most common brand-deal problem, and correcting the position takes real time.",
      "The formula may sit with a flavour house or a co-packer rather than with the brand; if it cannot move, what is being sold is a name and not a product.",
      "Artwork, dielines and product photography are frequently owned by designers and photographers who were paid but never signed an assignment.",
      "The GS1 company prefix and existing UPCs are issued to a specific legal entity and retailers match them against a vendor record, so how they are handled decides whether the buyer can ship to the same accounts.",
      "Existing inventory carries date codes and a label naming the seller's entity in the responsible-party line, so the buyer inherits a reprint deadline along with the stock.",
      "Retailer authorisations and distributor relationships do not automatically follow the brand; each one is a conversation, and some of them will be renegotiations.",
    ],
    founderMistakes: [
      "Selling a brand whose trademark was still registered in the founder's personal name.",
      "Discovering during diligence that the flavour house, not the brand, owned the formula.",
      "Having no signed assignment for the logo or the pack photography.",
      "Assuming subscriber data could simply be handed over with the rest of the assets.",
      "Never checking whether the co-packer would take the buyer on at all.",
    ],
    negotiationPoints: [
      "Ask for the asset list to be built early, with ownership evidence attached to each line.",
      "Ask for a defined process and timetable to cure the gaps you already know about.",
      "Ask that the buyer's co-packer and supplier conversations happen before closing rather than after.",
      "Ask for transition support to be scoped in hours and paid for beyond a stated point.",
      "Ask for a non-compete limited to the specific category and a defined period.",
    ],
    redFlags: [
      "A trademark not registered in the selling entity's exact name.",
      "No written formula ownership anywhere in the file.",
      "Artwork with no assignment from the designer.",
      "A subscriber list transferred with no consideration of consent.",
      "A non-compete covering all food and beverage.",
      "No transition period at all in a business only the founder knows how to run.",
    ],
    beforeYouSign: [
      "A written asset inventory with an ownership document behind every line.",
      "Trademark records checked against the selling entity's exact legal name.",
      "The formula ownership paperwork, including anything held by a flavour house or co-packer.",
      "Signed assignments from every designer, photographer and developer.",
      "Counsel's read on the customer and subscriber data transfer.",
    ],
    related: [
      "ip-purchase-agreement",
      "asset-purchase-agreement",
      "trademark-assignment",
      "formula-ownership-agreement",
      "packaging-artwork-ownership",
      "transition-services-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "A brand sale turns entirely on whether you can prove you own each asset being sold, and the trademark, formula and artwork chain of title is a legal review rather than a checklist.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "ip-purchase-agreement",
    name: "IP Purchase Agreement",
    category: "ma",
    subcategory: "Brand & IP deals",
    aliases: ["Intellectual Property Purchase Agreement", "IP Assignment and Purchase", "Formula Purchase Agreement"],
    stages: ["growth", "exit"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "IP Owner + Buyer",
    whenYouNeedIt:
      "When intellectual property is being sold on its own — a formula, a trademark, a recipe library or a portfolio of brand assets — without the operating business around it.",
    whatItDoes:
      "Transfers named intellectual property from one owner to another, with statements about what is owned, what has already been licensed to others, and what is known about third-party claims.",
    whyItMatters:
      "IP is the part of a CPG brand a buyer most wants and the part founders are least able to prove they own. Ownership comes from a chain of signed documents — from the founder, the food scientist, the designer, the developer, the agency — and a gap anywhere in that chain reduces what can actually be sold. The value here sits almost entirely in the diligence: a formula the flavour house also supplies to other clients, a logo the designer never assigned, or a trademark filed in the wrong name each turn a clean sale into a discount and a delay.",
    majorTerms: [
      "The IP being transferred, listed specifically",
      "Registrations, applications and their current status",
      "Chain-of-title documents supporting each item",
      "Existing licences and who else may use the IP",
      "Whether the seller retains any rights",
      "Statements about known third-party claims",
      "Price and payment terms",
      "Indemnification and its cap",
      "Post-closing cooperation on recordals and further assurances",
      "Confidentiality of know-how transferred",
    ],
    clausesToWatch: [
      {
        clause: "The specific list of what transfers and what is retained",
        why: "IP described in general terms leaves both sides believing different things about what was sold, and that argument only starts after closing.",
      },
      {
        clause: "Existing licences",
        why: "A flavour house supplying the same base to other clients is a real limit on what the buyer thinks it is getting exclusively.",
      },
      {
        clause: "Representations about third-party claims",
        why: "A pending objection or an unresolved conflict over a mark can make the asset unusable in exactly the market the buyer cares about.",
      },
      {
        clause: "Indemnity cap and survival",
        why: "Uncapped IP indemnity can exceed the price you received, long after the money has been spent.",
      },
      {
        clause: "Post-closing cooperation",
        why: "Recording transfers usually requires further signatures, and without an obligation to provide them the buyer is left chasing you.",
      },
      {
        clause: "Know-how and unregistered trade secrets",
        why: "Process parameters and sourcing detail appear in no register, so the transfer depends entirely on documentation that may sit with your co-packer.",
      },
    ],
    cpgIssues: [
      "A formula described as proprietary may belong to the flavour house that developed it, or may be non-exclusive to you, with nothing behind it but a quote and an invoice.",
      "Trade secrets — process parameters, ingredient sourcing, actual production settings — appear in no registry, so their transfer depends on documentation the co-packer may hold rather than you.",
      "Trademark registrations in the wrong entity name, or in classes that do not cover what you actually sell, are common and take months to correct.",
      "Packaging artwork, dielines, photography and video are separate rights from the trademark and are usually the least documented assets in the whole file.",
      "Where a co-packer contributed process improvements, ownership of those improvements is frequently unaddressed in the original co-packing agreement and becomes a live diligence question here.",
    ],
    founderMistakes: [
      "Selling a formula the flavour house also supplied to two competing brands.",
      "Presenting a trademark as registered when only an application existed.",
      "Having no assignment from the freelance designer who created the label.",
      "Forgetting that the co-packer held the only complete specification file.",
      "Giving unlimited IP warranties with no cap on a modest transaction.",
    ],
    negotiationPoints: [
      "Ask that the transfer list names each registration, application and asset individually.",
      "Ask for the chain-of-title documents to be assembled before the price is agreed.",
      "Ask for a defined post-closing cooperation obligation covering recordals.",
      "Ask for an indemnity cap proportionate to the price actually received.",
      "Ask that existing licences and non-exclusive arrangements are disclosed in writing.",
    ],
    redFlags: [
      "IP described in general terms rather than listed asset by asset.",
      "No chain-of-title documents available at all.",
      "An undisclosed licence to a third party.",
      "Unlimited IP indemnity.",
      "A formula held by a supplier with nothing in writing behind it.",
    ],
    beforeYouSign: [
      "Every assignment from every founder, contractor, designer and developer.",
      "The trademark register position checked against the seller's exact entity name.",
      "The formula ownership documentation, including anything held by a flavour house or co-packer.",
      "A written list of everyone who has ever been given the formula or the specification.",
    ],
    related: [
      "brand-acquisition-agreement",
      "trademark-assignment",
      "formula-ownership-agreement",
      "ip-assignment-agreement",
      "asset-purchase-agreement",
      "copyright-assignment",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Whether you can transfer IP at all depends on a chain of signed documents, and a gap in that chain is a legal problem counsel has to find before a buyer does.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "transition-services-agreement",
    name: "Transition Services Agreement",
    category: "ma",
    subcategory: "Post-closing",
    aliases: ["TSA", "Transition Support Agreement", "Post-Closing Services Agreement"],
    stages: ["exit"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Seller (or Founder) + Buyer",
    whenYouNeedIt:
      "At closing, when the buyer needs you or your team to keep the business running while they take it over — usually because the brand's operations live in a very small number of heads.",
    whatItDoes:
      "Sets out what services the seller provides after closing, for how long, at what cost, and what the buyer has to do to become self-sufficient.",
    whyItMatters:
      "In CPG the seller is often the only person who knows how the co-packer's line actually runs, which broker to call at which chain, and why one distributor's orders always arrive wrong. A TSA is the mechanism for transferring that, and founders routinely underestimate it: they agree to reasonable assistance for six months and end up running the business for free while somebody else makes the decisions. Scope, hours and an end date are the entire document. If an earn-out is also in place, the two interact — you may be obliged to help while your payout depends on results you no longer control.",
    majorTerms: [
      "Services listed specifically",
      "Hours or level of effort per week",
      "Duration and how any extension works",
      "Fees, and whether services are already inside the purchase price",
      "Named individuals providing the services",
      "Systems and account access",
      "Confidentiality",
      "Liability and standard of care",
      "What triggers the end of the arrangement",
      "Interaction with any earn-out or employment agreement",
    ],
    clausesToWatch: [
      {
        clause: "'Reasonable assistance' with no hours attached",
        why: "It is the most common way a founder ends up working unpaid for months after the sale has closed.",
      },
      {
        clause: "Duration and automatic extension",
        why: "An arrangement that renews unless you object keeps running through the season you had planned to be free.",
      },
      {
        clause: "Whether services are paid or included in the price",
        why: "Services folded into the purchase price mean you are working for money you have already been paid.",
      },
      {
        clause: "Named individuals",
        why: "If the agreement names people who may leave, the obligation to deliver falls back on whoever remains — often you.",
      },
      {
        clause: "Liability for mistakes made while assisting",
        why: "Helping with systems you no longer control creates exposure for errors you cannot prevent.",
      },
      {
        clause: "Interaction with an earn-out",
        why: "Obligations to assist and targets you are measured against have to be consistent, or you are working toward a number someone else is moving.",
      },
    ],
    cpgIssues: [
      "The transferable knowledge in a brand is operational: co-packer run parameters, ingredient lead times, which broker covers which chain, and each retailer's reset calendar. Write those into the service list rather than leaving them to 'assistance'.",
      "Retailer vendor records, distributor portals, EDI setups and marketplace seller accounts all have to be re-established in the buyer's name, and each has its own timetable and its own approval queue.",
      "A production run booked before closing may be delivered after it, so who manages that run, who pays for it and who owns the output has to be stated explicitly.",
      "Quality and complaint handling cannot pause: COAs, batch records and traceability have to continue without a gap while the buyer stands up their own process.",
      "Seasonality decides how heavy the transition is — one that starts in a quiet quarter looks easy, and one that starts in your peak is a full-time job you agreed to do for nothing.",
    ],
    founderMistakes: [
      "Agreeing open-ended assistance and working unpaid for months after closing.",
      "Not scoping the retailer and distributor account transfers, which took far longer than anyone expected.",
      "Leaving a pre-closing production run undefined, then arguing about who paid for it.",
      "Agreeing a TSA alongside an earn-out without reading the two documents against each other.",
      "Giving system access with no limit and being blamed for post-closing errors.",
    ],
    negotiationPoints: [
      "Ask for a specific, listed service scope with a maximum number of hours per week.",
      "Ask for a fixed end date, with extensions only by written agreement and only if paid.",
      "Ask for named deliverables the buyer must complete in order to become self-sufficient.",
      "Ask that anything beyond the agreed scope is paid at a stated rate.",
      "Ask that your obligations here and any earn-out targets are consistent with each other.",
    ],
    redFlags: [
      "'Reasonable assistance as requested' with no cap of any kind.",
      "No end date.",
      "Unpaid services extending beyond a few weeks.",
      "An obligation to remain available with no compensation attached.",
      "A TSA that makes you responsible for outcomes you no longer control.",
    ],
    beforeYouSign: [
      "An honest list of everything only you know how to do.",
      "A view of what your peak season would demand of you during the transition period.",
      "The earn-out terms read alongside the TSA rather than separately.",
      "A written handover plan with dates and named owners on the buyer's side.",
    ],
    related: [
      "asset-purchase-agreement",
      "ma-stock-purchase-agreement",
      "earn-out-agreement",
      "brand-acquisition-agreement",
      "letter-of-intent",
      "consultant-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "A TSA is where a clean sale quietly turns into unpaid employment, so its scope has to be drafted alongside the purchase agreement and any earn-out rather than after them.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "due-diligence-nda",
    name: "Due Diligence NDA",
    category: "ma",
    subcategory: "Deal process",
    aliases: ["M&A NDA", "Confidentiality Agreement (Transaction)", "Data Room NDA"],
    stages: ["growth", "exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Prospective Buyer",
    whenYouNeedIt:
      "Before you send anything to a prospective buyer — the first document in a sale process, signed before the first data room invitation goes out.",
    whatItDoes:
      "Restricts what a prospective buyer may do with what you show them: the formula, cost sheets, co-packer identity, retailer terms, velocity data and customer lists.",
    whyItMatters:
      "Most people who look at your brand will not buy it, and several of them will be competitors, distributors or investors in competing brands. Diligence hands them your landed cost, your co-packer, your trade spend and your account-level velocity — a competitive briefing you would never give voluntarily. The protections that matter are non-use as well as non-disclosure, a defined list of who may see the material, a non-solicit covering your team and your co-packer, a return-or-destroy obligation, and a staged disclosure plan so the most sensitive material only goes out once the deal is real.",
    majorTerms: [
      "Definition of confidential information",
      "Permitted purpose, limited to evaluating the transaction",
      "Who may receive the information and their obligations",
      "Non-solicitation of employees and key suppliers",
      "Standstill, where relevant",
      "Return or destruction of materials",
      "Term of the obligations",
      "Whether the existence of discussions is itself confidential",
      "Remedies",
    ],
    clausesToWatch: [
      {
        clause: "Residuals language",
        why: "It allows the recipient to use whatever their people remember, which for a formula or a cost structure is most of what matters.",
      },
      {
        clause: "The permitted recipient list",
        why: "If it extends to affiliates and portfolio companies without limit, your data reaches every brand the buyer is involved with.",
      },
      {
        clause: "Non-solicitation scope",
        why: "Without a non-solicit covering your co-packer and key suppliers, a failed buyer can walk away with your production slot instead of your business.",
      },
      {
        clause: "Term of the obligations",
        why: "A confidentiality term of a year or two ends long before the value of a formula does.",
      },
      {
        clause: "Return or destruction",
        why: "Without an obligation covering the data room and every copy taken from it, your material simply stays with them.",
      },
      {
        clause: "Confidentiality of the discussions themselves",
        why: "Word that a brand is for sale reaches distributors and retailers quickly and changes how they treat you.",
      },
    ],
    cpgIssues: [
      "The formula, process parameters and ingredient sourcing are trade secrets whose value depends entirely on staying secret, so a short confidentiality term expires while the asset is still valuable.",
      "Your co-packer's identity and available capacity is competitive information: a competing brand that learns it may take your production slot.",
      "Landed cost, trade spend and deduction rates by retailer tell a competitor exactly how to price against you on the same shelf.",
      "Account-level velocity and distributor performance data is the most valuable material in your data room and the least protected by habit.",
      "Buyers who are strategic operators may already sell into the same accounts, so the permitted-recipient list and the non-solicit have to be written with that specific risk in mind.",
    ],
    founderMistakes: [
      "Sending the full cost sheet on the first call, before anything was signed.",
      "Signing a one-way NDA in the buyer's favour when both sides were sharing information.",
      "Accepting a residuals clause without understanding what it permitted.",
      "Giving a competing operator the co-packer's name early in the process.",
      "Using an NDA whose term was far shorter than the useful life of the formula.",
    ],
    negotiationPoints: [
      "Ask for non-use as well as non-disclosure, stated as separate obligations.",
      "Ask for a named recipient list, with written obligations flowing down to advisers.",
      "Ask for a non-solicit covering employees, your co-packer and your key suppliers.",
      "Ask to stage disclosure so formula and cost detail come last, after terms are agreed.",
      "Ask for return or destruction with written confirmation, including the data room.",
    ],
    redFlags: [
      "A residuals clause.",
      "No non-use provision at all.",
      "Permitted recipients extending to affiliates and portfolio companies without limit.",
      "A one- or two-year term on formula and trade secret information.",
      "No obligation to return or destroy anything.",
      "A buyer who will not sign anything before seeing 'just a bit more'.",
    ],
    beforeYouSign: [
      "A staged disclosure plan naming exactly what goes out at each stage.",
      "A list of what you will not disclose at all before a signed purchase agreement.",
      "A redaction plan for co-packer, supplier and customer names in the early stages.",
    ],
    related: [
      "mutual-nda",
      "letter-of-intent",
      "no-shop-agreement",
      "investor-nda",
      "trade-secret-protection-agreement",
      "asset-purchase-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "no-shop-agreement",
    name: "Exclusivity / No-Shop Agreement",
    category: "ma",
    subcategory: "Deal process",
    aliases: ["No-Shop", "Exclusivity Agreement", "Standstill and Exclusivity Letter"],
    stages: ["exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company and Founders + Buyer",
    whenYouNeedIt:
      "When a buyer asks you to stop talking to anyone else while they complete diligence — usually inside the LOI, sometimes as a standalone document.",
    whatItDoes:
      "Commits you, for a defined period, not to solicit or negotiate with other buyers, and usually to tell the buyer if someone approaches you.",
    whyItMatters:
      "Exclusivity is the single largest concession in a sale process, and it is given at the moment you have the most leverage and the least information. Once the market is closed, the buyer's incentive to hold the price disappears; retrading after diligence is common, and a seller with no alternative has nothing to answer with. The length of the period is the whole negotiation — long enough for genuine diligence, short enough that the buyer has to work, with a hard expiry and no automatic extension.",
    majorTerms: [
      "Length of the exclusivity period",
      "What activity is prohibited",
      "Notification obligation on inbound approaches",
      "Whether any extension is automatic",
      "Milestones the buyer must hit",
      "Termination rights",
      "Costs and expenses",
      "Confidentiality",
      "What happens if the buyer changes price or structure",
    ],
    clausesToWatch: [
      {
        clause: "Automatic extension",
        why: "An extension at the buyer's option means the period is effectively as long as they want it to be.",
      },
      {
        clause: "Absence of buyer milestones",
        why: "Without dates for the diligence list, the draft agreement and the confirmatory work, there is nothing forcing the buyer to move at all.",
      },
      {
        clause: "Prohibited activity, written broadly",
        why: "Language that catches ordinary financing conversations can leave you unable to raise working capital while the process drags on.",
      },
      {
        clause: "Termination on a retrade",
        why: "Without a right to walk when the buyer cuts the price, you stay locked to a deal that is no longer the deal you agreed.",
      },
      {
        clause: "Notification obligations",
        why: "An obligation to report every approach can chill other interest permanently, well beyond the exclusivity period.",
      },
      {
        clause: "Expense reimbursement",
        why: "Paying the buyer's costs for a process that fails turns a disappointment into a bill.",
      },
    ],
    cpgIssues: [
      "Timing against your season matters more here than in most categories: exclusivity running across your peak takes you off the market during the months your numbers look best.",
      "Retailer reset calendars and distributor annual reviews do not pause for a deal, so exclusivity that blocks a strategic conversation can cost you a listing as well as a bidder.",
      "Broadly written prohibitions can also block the financing conversation you need if the process drags and working capital runs down mid-season.",
      "Buyers who are trade operators sometimes use diligence to learn, and a long exclusivity with no milestones gives a competitor months of access with no obligation to close.",
      "A brand that has just won a large authorisation is at its most valuable, which is precisely when buyers ask for the longest lock-up.",
    ],
    founderMistakes: [
      "Agreeing a long exclusivity with automatic extensions and losing an entire selling season.",
      "Not carving out financing conversations and running short of working capital mid-process.",
      "Having no right to walk when the buyer cut the price after diligence.",
      "Agreeing to notify the buyer of every approach, which permanently chilled other interest.",
      "Signing exclusivity before counsel had read the termination mechanics.",
    ],
    negotiationPoints: [
      "Ask for the shortest period that allows genuine diligence, with a hard expiry.",
      "Ask for buyer milestones, with exclusivity ending automatically if they are missed.",
      "Ask for a carve-out permitting financing conversations.",
      "Ask for the right to terminate immediately if the buyer changes price or structure.",
      "Ask that any extension requires your written agreement rather than their request.",
    ],
    redFlags: [
      "Automatic extension on the buyer's request.",
      "No milestones of any kind.",
      "No right to terminate on a retrade.",
      "An obligation to report every approach with nothing reciprocal.",
      "A period running through your peak season.",
      "Expense reimbursement payable regardless of outcome.",
    ],
    beforeYouSign: [
      "Your own calendar of season, retailer resets and cash needs across the proposed period.",
      "The buyer's diligence list and their stated timetable.",
      "A clear view of which other conversations you are giving up by signing.",
      "Counsel's read of the termination and extension mechanics.",
    ],
    related: [
      "letter-of-intent",
      "due-diligence-nda",
      "asset-purchase-agreement",
      "ma-stock-purchase-agreement",
      "term-sheet",
      "investor-letter-of-intent",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Exclusivity binds you when most of the LOI does not, and its length, milestones and termination rights decide how much leverage you keep for the rest of the process.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "earn-out-agreement",
    name: "Earn-Out Agreement",
    category: "ma",
    subcategory: "Post-closing",
    aliases: ["Earnout", "Contingent Consideration Agreement", "Performance Payment Agreement"],
    stages: ["exit"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Seller (Founders) + Buyer",
    whenYouNeedIt:
      "When the buyer will not pay your number today and offers to pay part of it later if the brand performs — extremely common in CPG, where growth rates and distribution gains are hard to price.",
    whatItDoes:
      "Defers part of the purchase price and makes it conditional on the business hitting defined targets over a defined period, with a formula for calculating what is owed.",
    whyItMatters:
      "An earn-out asks you to keep delivering results in a business you no longer control. The buyer sets the trade spend, chooses which SKUs get produced, decides whether to keep your distributor, and decides whether to push your brand or their own into the same shelf set — and every one of those decisions moves your number. Founders accept earn-outs because they close the gap on price. The ones who are satisfied afterwards are the ones who defined the metric precisely, secured operating protections in writing, and treated the earn-out as a bonus rather than as part of the price they were counting on.",
    majorTerms: [
      "The metric: revenue, gross profit, contribution, volume or distribution points",
      "Exactly how the metric is defined and calculated",
      "Measurement period and payment dates",
      "Targets, and whether payment is a cliff or a sliding scale",
      "Operating covenants protecting the business during the period",
      "The founder's role and actual decision authority",
      "Access to the books and a dispute mechanism",
      "Acceleration if the buyer resells or discontinues the brand",
      "Set-off against indemnity claims",
      "Cap on the total earn-out",
    ],
    clausesToWatch: [
      {
        clause: "The metric definition",
        why: "Gross revenue, net revenue after deductions and gross profit produce wildly different outcomes, and the deduction line is exactly where a brand's revenue gets restated.",
      },
      {
        clause: "Operating covenants",
        why: "Without commitments on support levels, pricing, SKU continuity and distributor relationships, the buyer can reduce your number through ordinary business decisions.",
      },
      {
        clause: "Set-off against indemnity claims",
        why: "It lets the buyer deduct claims from money you earned, which turns a performance payment into a negotiating chip.",
      },
      {
        clause: "Acceleration on a resale or discontinuation",
        why: "If the buyer sells the brand on or shelves it, your target becomes unreachable through no fault of the business.",
      },
      {
        clause: "The founder's actual authority during the period",
        why: "A title without control over trade spend and production means you are accountable for results you cannot influence.",
      },
      {
        clause: "Dispute mechanism and audit access",
        why: "Without defined access to the numbers and a named process, you are arguing about a calculation only the buyer can see.",
      },
    ],
    cpgIssues: [
      "Trade spend, slotting, deductions, spoils and free fill all sit between gross and net revenue, so a buyer who increases promotional support can grow your volume while shrinking the number your earn-out is measured against.",
      "A buyer holding a competing SKU in the same shelf set controls which one gets pushed at a retailer reset, so a covenant on support and shelf position is worth more than an extra point on the target.",
      "Discontinuing a SKU, changing a pack format or moving production to the buyer's own facility can all reduce the measured result for reasons unconnected to consumer demand.",
      "Distributor changes made by the buyer — consolidating into their own network, or terminating your DSD partner — reset velocity in ways no target anticipated.",
      "Supply interruption at a co-packer the buyer selected can make a target unreachable, so a provision addressing supply failure is worth asking for explicitly.",
      "Earn-out structures, lengths and metrics vary widely by category, deal size and buyer type, so treat any figure quoted to you as market practice as one buyer's preference rather than a standard.",
    ],
    founderMistakes: [
      "Agreeing an earn-out measured on net revenue and then watching promotional spend rise.",
      "Accepting no operating covenants at all because the buyer said they were committed to the brand.",
      "Staying on with a title but no authority over trade spend or production planning.",
      "Not securing acceleration when the buyer sold the brand on eighteen months later.",
      "Treating the earn-out as part of the price when planning their own finances after closing.",
    ],
    negotiationPoints: [
      "Ask for a metric as close to the top line as possible, or a gross profit calculation with fixed cost assumptions written in.",
      "Ask for written operating covenants covering support levels, SKU continuity, pricing and distributor relationships.",
      "Ask for acceleration on a resale, a discontinuation or a material change in strategy.",
      "Ask for defined access to records and a named dispute process with a timetable.",
      "Ask that indemnity set-off is excluded or tightly capped.",
      "Ask for a sliding scale rather than an all-or-nothing cliff.",
    ],
    redFlags: [
      "A metric measured after costs the buyer controls.",
      "No operating covenants of any kind.",
      "Unlimited set-off against the earn-out.",
      "A target requiring growth well beyond your current trajectory.",
      "No acceleration if the buyer resells or discontinues the brand.",
      "A measurement period longer than your visibility into the category.",
    ],
    beforeYouSign: [
      "A model of the earn-out under a bad, base and good case, and a decision that you can accept the bad case.",
      "The buyer's stated plans for the brand, in writing.",
      "The operating covenants read alongside your employment or transition terms.",
      "Counsel and an accountant on the metric definition before it is agreed.",
    ],
    related: [
      "letter-of-intent",
      "asset-purchase-agreement",
      "ma-stock-purchase-agreement",
      "transition-services-agreement",
      "membership-interest-purchase-agreement",
      "brand-acquisition-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "An earn-out is a promise of money conditioned on results you no longer control, and only carefully drafted metric definitions and operating covenants make it worth anything.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default MA_DOCS;
